import {
  type Basket,
  type DataResponse,
  logger,
  POST,
  request,
  useCallbackRef,
  useEndpointsContext,
  useGlobalToasts,
} from '@talos/kyoko';
import { get, isEqual, keys, values } from 'lodash-es';
import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';
import { OrgConfigurationKey, useOrgConfiguration } from './OrgConfigurationProvider';

interface BasketsContextProps {
  baskets: Basket[] | undefined;
  upsertBaskets: (baskets: Basket[]) => Promise<void>;
  /** Call to have the backend refresh the baskets. Will update the state of the provider to keep the new baskets. */
  apiRefreshBaskets: () => Promise<void>;
}

const BasketsContext = createContext<BasketsContextProps | null>(null);
BasketsContext.displayName = 'BasketsContext';

export function useBaskets() {
  const context = useContext(BasketsContext);
  if (context == null) {
    throw new Error('Missing BasketsContext.Provider further up in the tree. Did you forget to add it?');
  }
  return context;
}

export function BasketsProvider({ children }: PropsWithChildren) {
  const { add: addToast } = useGlobalToasts();
  const { orgApiEndpoint } = useEndpointsContext();
  const basketsEndpoint = `${orgApiEndpoint}/baskets`;

  const baskets = useOrgConfigBaskets();

  const upsertBaskets = useCallbackRef(async (upsertBaskets: Basket[]) => {
    const requests = upsertBaskets.map(b => request<DataResponse<Basket[]>>(POST, `${basketsEndpoint}/`, b));
    const results = await Promise.allSettled(requests);
    const successes = results.filter(r => r.status === 'fulfilled');
    const failures = results.filter(r => r.status === 'rejected');

    if (successes.length > 0) {
      addToast({
        text: `Successfully imported ${successes.length} rows`,
        variant: 'POSITIVE',
      });
    }

    failures.forEach(f => {
      addToast({
        text: `Failed to import row: ${f.reason}`,
        variant: 'NEGATIVE',
      });
    });
  });

  const apiRefreshBaskets = useCallbackRef(() => {
    return request<DataResponse<Basket>>(POST, `${orgApiEndpoint}/unqork/baskets`)
      .then(res => {
        addToast({
          variant: 'POSITIVE',
          text: 'Successfully imported basket details.',
        });
      })
      .catch((e: ErrorEvent) => {
        addToast({
          variant: 'NEGATIVE',
          text: `Failed to import basket details: ${e.message}`,
        });
      });
  });

  const value = useMemo(
    () => ({ baskets, upsertBaskets, apiRefreshBaskets }),
    [baskets, upsertBaskets, apiRefreshBaskets]
  );

  return <BasketsContext.Provider value={value}>{children}</BasketsContext.Provider>;
}

/** Org config basket definition type */
interface BasketDefinition {
  BasketSymbol: string;
  UnderlyingSecuritySymbol: string;
}

/** Org config basket quantity details type */
interface BasketQuantityDetails {
  BasketSymbol: string;
  BasketRatio: string;
  BasketRatioDate: string;
}

// Reads the basket definitions from the
const useOrgConfigBaskets = (): Basket[] => {
  const { getConfig } = useOrgConfiguration();

  const baskets = useMemo(() => {
    const stringifiedBasketDefs = getConfig(OrgConfigurationKey.BasketDefinitions, '');
    const stringifiedBasketQtyDetails = getConfig(OrgConfigurationKey.BasketQuantityDetails, '');

    if (stringifiedBasketDefs === '' || stringifiedBasketQtyDetails === '') {
      return [];
    }

    try {
      const defs: Record<string, BasketDefinition> = JSON.parse(stringifiedBasketDefs);
      const qtyDetails: Record<string, BasketQuantityDetails> = JSON.parse(stringifiedBasketQtyDetails);

      if (!isEqual(keys(defs), keys(qtyDetails))) {
        logger.error(new Error('basket.Definitions, basket.QuantityDetails are out of sync'), {
          extra: { defs, qtyDetails },
        });
        return [];
      }

      return values(defs).map(value => {
        // grab the qty details from the qty details record
        const quantities = get(qtyDetails, value.BasketSymbol);

        return {
          BasketSymbol: value.BasketSymbol,
          UnderlyingSecuritySymbol: value.UnderlyingSecuritySymbol,
          QuantityDetails: {
            BasketRatio: quantities.BasketRatio,
            BasketRatioDate: quantities.BasketRatioDate,
          },
        } satisfies Basket;
      });
    } catch (e) {
      logger.error(new Error('Failed to parse basket.Definitions, basket.QuantityDetails'), {
        extra: { stringifiedBasketDefs, stringifiedBasketQtyDetails },
      });
      return [];
    }
  }, [getConfig]);

  return baskets;
};
